import { ATTACK_KINDS, RIDES, WORLD } from './config.js';
import type { AttackKind, Ride } from './config.js';

export type Vec3 = [number, number, number];
export type Quat = [number, number, number, number];

/** Rider movement mode shared over the wire. `down` is server-owned (knocked out). */
export const MODE = { ground: 0, air: 1, grind: 2, bail: 3, down: 4 } as const;
export type RiderMode = (typeof MODE)[keyof typeof MODE];

/** Pose flag bits that let remote clients animate a rider. */
export const FLAG = {
  crouch: 1,
  grab: 2,
  flip: 4,
  punch: 8,
  kick: 16,
  push: 32,
  vert: 64,
  brake: 128,
} as const;

export interface RiderState {
  /** Feet position (m). */
  p: Vec3;
  /** Body orientation quaternion (x, y, z, w). */
  q: Quat;
  /** Velocity (m/s). */
  v: Vec3;
  m: RiderMode;
  /** FLAG bitfield. */
  f: number;
  /** Trick code, see tricks.ts. */
  k: number;
  /** Signed animation scalar: grind balance or trick phase, in [-1, 1]. */
  a: number;
}

export interface SpawnPoint {
  p: Vec3;
  yaw: number;
}

export interface HelloRequest {
  v: number;
  name: string;
  ride: Ride;
  token: string | null;
}

export interface RosterEntry {
  id: string;
  name: string;
  ride: Ride;
  color: number;
  kos: number;
  wipeouts: number;
  linked: boolean;
  bot: boolean;
}

export interface Welcome {
  id: string;
  name: string;
  ride: Ride;
  color: number;
  spawn: SpawnPoint;
  riders: RosterEntry[];
  coins: number | null;
  maxRiders: number;
  serverTime: number;
}

export type HelloError = 'full' | 'version' | 'invalid' | 'joined' | 'rate';
export type HelloResult = { ok: true; welcome: Welcome } | { ok: false; error: HelloError };

export interface RiderSnapshot extends RiderState {
  id: string;
  hp: number;
  /** Spawn protection active. */
  prot: boolean;
}

export interface Snapshot {
  t: number;
  riders: RiderSnapshot[];
}

export interface AttackRequest {
  kind: AttackKind;
}

export interface RideRequest {
  ride: Ride;
}

export interface HitEvent {
  from: string;
  to: string;
  kind: AttackKind;
  dmg: number;
  hp: number;
  /** Knockback velocity applied to the target (m/s). */
  kb: Vec3;
  ko: boolean;
}

export interface RespawnEvent {
  id: string;
  spawn: SpawnPoint;
  hp: number;
}

export type FeedEvent =
  | { kind: 'join'; name: string }
  | { kind: 'leave'; name: string }
  | { kind: 'ko'; by: string; victim: string; attack: AttackKind };

export interface AwardEvent {
  coins: number;
  total: number;
}

export interface ServerStatus {
  ok: true;
  /** Human riders only; bots never consume player capacity. */
  riders: number;
  bots: number;
  maxRiders: number;
}

export interface ClientToServerEvents {
  hello: (payload: unknown, ack: (result: HelloResult) => void) => void;
  state: (payload: unknown) => void;
  attack: (payload: unknown) => void;
  ride: (payload: unknown) => void;
  reset: () => void;
}

export interface ServerToClientEvents {
  snap: (payload: Snapshot) => void;
  roster: (payload: RosterEntry[]) => void;
  hit: (payload: HitEvent) => void;
  respawn: (payload: RespawnEvent) => void;
  feed: (payload: FeedEvent) => void;
  award: (payload: AwardEvent) => void;
}

const COORD_LIMIT = 10_000;
const TOKEN_MAX = 4_096;
const RAW_NAME_MAX = 64;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function finiteTuple(value: unknown, length: number, limit: number): number[] | null {
  if (!Array.isArray(value) || value.length !== length) return null;
  const out: number[] = [];
  for (const entry of value) {
    if (typeof entry !== 'number' || !Number.isFinite(entry) || Math.abs(entry) > limit) {
      return null;
    }
    out.push(entry);
  }
  return out;
}

function intInRange(value: unknown, min: number, max: number): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max
    ? value
    : null;
}

export function isRide(value: unknown): value is Ride {
  return typeof value === 'string' && (RIDES as readonly string[]).includes(value);
}

export function isAttackKind(value: unknown): value is AttackKind {
  return typeof value === 'string' && (ATTACK_KINDS as readonly string[]).includes(value);
}

/** Collapses whitespace, strips control characters and clamps to the display limit. */
export function cleanName(value: unknown, max: number = WORLD.nameMaxLength): string {
  if (typeof value !== 'string') return '';
  const visible = [...value]
    .map((character) => {
      const code = character.codePointAt(0) ?? 0;
      return code <= 0x1f || (code >= 0x7f && code <= 0x9f) ? ' ' : character;
    })
    .join('')
    .replace(/\s+/gu, ' ')
    .trim();
  return [...visible].slice(0, max).join('').trim();
}

export function decodeHello(raw: unknown): HelloRequest | null {
  if (!isRecord(raw)) return null;
  const v = intInRange(raw.v, 0, 1_000);
  if (v === null || !isRide(raw.ride)) return null;
  if (typeof raw.name !== 'string' || raw.name.length > RAW_NAME_MAX) return null;
  let token: string | null = null;
  if (typeof raw.token === 'string' && raw.token.length > 0) {
    if (raw.token.length > TOKEN_MAX) return null;
    token = raw.token;
  } else if (raw.token !== undefined && raw.token !== null && raw.token !== '') {
    return null;
  }
  return { v, name: raw.name, ride: raw.ride, token };
}

export function decodeState(raw: unknown): RiderState | null {
  if (!isRecord(raw)) return null;
  const p = finiteTuple(raw.p, 3, COORD_LIMIT);
  const q = finiteTuple(raw.q, 4, 2);
  const v = finiteTuple(raw.v, 3, COORD_LIMIT);
  const m = intInRange(raw.m, 0, 4);
  const f = intInRange(raw.f, 0, 255);
  const k = intInRange(raw.k, 0, 31);
  if (!p || !q || !v || m === null || f === null || k === null) return null;
  if (typeof raw.a !== 'number' || !Number.isFinite(raw.a)) return null;
  const length = Math.hypot(q[0]!, q[1]!, q[2]!, q[3]!);
  if (length < 0.5) return null;
  return {
    p: [p[0]!, p[1]!, p[2]!],
    q: [q[0]! / length, q[1]! / length, q[2]! / length, q[3]! / length],
    v: [v[0]!, v[1]!, v[2]!],
    m: m as RiderMode,
    f,
    k,
    a: Math.max(-1, Math.min(1, raw.a)),
  };
}

export function decodeAttack(raw: unknown): AttackRequest | null {
  if (!isRecord(raw) || !isAttackKind(raw.kind)) return null;
  return { kind: raw.kind };
}

export function decodeRide(raw: unknown): RideRequest | null {
  if (!isRecord(raw) || !isRide(raw.ride)) return null;
  return { ride: raw.ride };
}

/** Rounds a number for compact JSON on the wire. */
export function round(value: number, digits = 3): number {
  const scale = 10 ** digits;
  return Math.round(value * scale) / scale;
}
